/** Passkeys im Browser (MP-E): Registrieren über /api/konto/passkeys, Anmelden über /api/auth/passkey. Ohne Fremdbibliothek. */
import { api, rufe } from "./client";

function b64url(buf: ArrayBuffer): string {
  let s = "";
  for (const x of new Uint8Array(buf)) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function b64urlDecode(s: string): Uint8Array {
  const n = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(n);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function passkeysMoeglich(): boolean {
  return typeof window !== "undefined" && "PublicKeyCredential" in window && !!navigator.credentials;
}

interface Optionen {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: "public-key"; alg: number }[];
  excludeCredentials: { type: "public-key"; id: string }[];
  authenticatorSelection: AuthenticatorSelectionCriteria;
  timeout: number;
}

/** Neuen Passkey anlegen (angemeldet). Liefert den Namen oder wirft mit verständlicher Meldung. */
export async function passkeyRegistrieren(name = ""): Promise<{ id: string; name: string }> {
  const o = await rufe<Optionen>(api.api.konto.passkeys.optionen.$post());
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: b64urlDecode(o.challenge) as BufferSource,
      rp: o.rp,
      user: { id: new TextEncoder().encode(o.user.id) as BufferSource, name: o.user.name, displayName: o.user.displayName },
      pubKeyCredParams: o.pubKeyCredParams,
      excludeCredentials: o.excludeCredentials.map((e) => ({ type: "public-key" as const, id: b64urlDecode(e.id) as BufferSource })),
      authenticatorSelection: o.authenticatorSelection,
      timeout: o.timeout,
      attestation: "none",
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Abgebrochen.");
  const r = cred.response as AuthenticatorAttestationResponse & { getPublicKey?: () => ArrayBuffer | null; getPublicKeyAlgorithm?: () => number; getAuthenticatorData?: () => ArrayBuffer; getTransports?: () => string[] };
  const pub = r.getPublicKey?.();
  const authData = r.getAuthenticatorData?.();
  if (!pub || !authData || !r.getPublicKeyAlgorithm) throw new Error("Dieser Browser liefert den Passkey nicht in einem Format, das wir prüfen können. Bitte Browser aktualisieren.");
  return rufe<{ id: string; name: string }>(
    api.api.konto.passkeys.$post({
      json: { id: cred.id, publicKey: b64url(pub), alg: r.getPublicKeyAlgorithm(), clientDataJSON: b64url(r.clientDataJSON), authenticatorData: b64url(authData), transports: r.getTransports?.() ?? [], name },
    }),
  );
}

/** Mit Passkey anmelden (nicht angemeldet). Liefert das Ziel, zu dem navigiert werden soll. */
export async function passkeyAnmelden(ziel: "laden" | "mieter" | "admin", laden?: string): Promise<{ ziel: string; zweiterFaktor: boolean }> {
  const o = await rufe<{ challenge: string; rpId: string; userVerification: UserVerificationRequirement; timeout: number }>(api.api.auth.passkey.optionen.$post());
  const cred = (await navigator.credentials.get({
    publicKey: { challenge: b64urlDecode(o.challenge) as BufferSource, rpId: o.rpId, userVerification: o.userVerification, timeout: o.timeout, allowCredentials: [] },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Abgebrochen.");
  const r = cred.response as AuthenticatorAssertionResponse;
  return rufe<{ ok: true; ziel: string; ladenId: string | null; zweiterFaktor: boolean }>(
    api.api.auth.passkey.anmelden.$post({ json: { id: cred.id, clientDataJSON: b64url(r.clientDataJSON), authenticatorData: b64url(r.authenticatorData), signature: b64url(r.signature), ziel, laden } }),
  );
}
